import React, { useEffect, useRef, useState } from 'react';
import { countPageHit } from '@/lib/marketing';
import { useNavigate } from 'react-router-dom';

// The "I cast projects" half of the booth QR code.
//
// Follows the handoff's producer landing: a hero that asks one question and holds
// four cards and nothing else, a detail panel that only exists once you answer,
// and then the rest of the page. Nothing is preselected on purpose — the page is a
// question, not a brochure.
//
// The ten image slots in the design have no assets yet. Rather than ship ten grey
// holes, sections collapse to their copy; each slot is marked in the code so an
// image drops straight in.

const INK = 'oklch(0.18 0.005 70)';
const INK_BODY = 'oklch(0.32 0.006 70)';
const INK_MUTED = 'oklch(0.48 0.006 70)';
const HAIRLINE = 'oklch(0.18 0.005 70 / 0.14)';
const CREAM = '#fbf3e5';
const YELLOW = '#ffd600';
const YELLOW_HOVER = '#ffe566';

// Show details. Config rather than literals — the booth line disappears until
// there is a number, and the whole block goes once the floor closes.
const BOOTH_NUMBER = '';           // still TBC
const SHOW_SPOTLIGHT = true;
// The project creation tool, in the app rather than a guessed link into casting.
// /find-talent is Katia: she builds the casting brief with you and posts the
// project, which is the same promise this page's conversion card makes. It has
// been live on main since Aug 17 with nothing linking to it — this is its first
// front door. Behind RequireAuth, so a signed-out producer meets the auth gate
// first, which they would need anyway to own a project.
const PROJECT_TOOL = '/find-talent';

const mono: React.CSSProperties = {
  fontFamily: 'var(--hj-font-mono, ui-monospace, monospace)',
  fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 500,
};

const Swipe: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{
    display: 'inline-block', padding: '0.02em 0.16em', background: YELLOW,
    clipPath: 'polygon(2% 8%, 98% 0%, 100% 92%, 0% 100%)', transform: 'rotate(-1.5deg)',
  }}>{children}</span>
);

type Track = {
  key: string; num: string; title: string;
  panelTitle: string; steps: string[]; cta: string;
  /** leaves the page instead of scrolling to the conversion card */
  ctaTo?: string;
  /** design's image slot id — drop the asset in here when one exists */
  slot: string;
  /** public path, once there is a real asset for the slot */
  image?: string;
};

const TRACKS: Track[] = [
  {
    key: 'cast', num: '01', title: 'Casting a project', slot: 'hmc-panel-cast',
    panelTitle: 'Describe it once. The breakdown builds itself.',
    steps: ['Actors, real people, specialists', 'Union or non-union, local only, any must-have', 'Photo or full tape submissions'],
    cta: 'Build my breakdown',
  },
  {
    key: 'shoot', num: '02', title: 'Shooting a project', slot: 'hmc-panel-shoot',
    image: '/brand/hmc-panel-shoot.jpg',
    panelTitle: 'We can run the shoot, wherever the people are.',
    steps: ['Remote-directed, local-quality capture', 'No full travel crew', 'Recorded and transcribed as you go'],
    cta: 'Talk through my shoot',
  },
  {
    key: 'acting', num: '03', title: 'Acting in projects', slot: 'hmc-panel-acting',
    image: '/brand/hmc-panel-acting.jpg',
    panelTitle: 'Get seen by the people casting here.',
    steps: ['Build a profile once, submit all year', 'Self-tape from your phone', 'Local breakdowns, not a national feed'],
    cta: 'Set up my profile',
    ctaTo: 'talent',
  },
  {
    key: 'testimonials', num: '04', title: 'Gathering testimonials', slot: 'hmc-panel-testimonials',
    image: '/brand/hmc-panel-testimonials.jpg',
    panelTitle: 'The hard part is the twentieth one, not the first.',
    steps: ['Send a link, they book a slot', 'Directed live, so you get the answer', 'Same light and framing every time'],
    cta: 'Plan my batch',
  },
];

const HoustonProducersPage: React.FC = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const agentRef = useRef<HTMLDivElement | null>(null);

  const track = TRACKS.find(t => t.key === active) || null;
  const talentHref = window.location.pathname.startsWith('/hmc') ? '/hmc' : '/houston';

  useEffect(() => {
    if (track && panelRef.current) panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [track]);

  // The producers card is a different piece of printed paper than the talent one,
  // so it gets its own count rather than folding into /hmc.
  useEffect(() => { countPageHit('hmc-producers'); }, []);

  const section: React.CSSProperties = { marginTop: 64 };
  const h2: React.CSSProperties = {
    fontWeight: 800, fontSize: 'clamp(28px, 3.8vw, 38px)', lineHeight: 1.08,
    letterSpacing: '-0.03em', color: INK, margin: '8px 0 0',
  };
  const pill = (light: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', minHeight: 40, padding: '0 16px',
    borderRadius: 999, fontSize: 13.5, fontWeight: 600,
    background: light ? '#fff' : 'transparent',
    border: `1px solid ${light ? HAIRLINE : 'rgba(251,243,229,0.3)'}`,
    color: light ? INK : CREAM,
  });

  return (
    <div style={{ background: CREAM, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>

        {/* Header */}
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <img src="/brand/wordmark-heyjoe-sm.png" alt="Hey Joe" style={{ height: 32, width: 'auto' }} />
            <span style={{ width: 1, height: 28, background: HAIRLINE }} />
            <img src="/brand/houston-media-conference.jpeg" alt="Houston Media Conference"
                 style={{ height: 44, width: 44, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => navigate(talentHref)}
                    style={{ ...pill(true), cursor: 'pointer', fontFamily: 'inherit' }}>
              ← Who are you?
            </button>
            <span style={{ ...mono, color: INK_MUTED }}>Houston · Sept 17–20, 2026</span>
          </div>
        </header>

        {/* Hero — the question and the four cards, nothing else on arrival */}
        <section style={{ minHeight: '86vh', display: 'grid', alignContent: 'center', gap: 34 }}>
          <h1 style={{
            fontWeight: 800, fontSize: 'clamp(46px, 9vw, 104px)', lineHeight: 0.98,
            letterSpacing: '-0.04em', color: INK, margin: 0,
          }}>
            What are you <Swipe>working on</Swipe>?
          </h1>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {TRACKS.map(t => {
              const on = active === t.key || hovered === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActive(t.key)}
                  onMouseEnter={() => setHovered(t.key)}
                  onMouseLeave={() => setHovered(h => (h === t.key ? null : h))}
                  style={{
                    minHeight: 196, borderRadius: 24, padding: '26px 24px 28px',
                    background: on ? YELLOW : '#fff',
                    border: `1px solid ${on ? YELLOW : HAIRLINE}`,
                    display: 'grid', alignContent: 'space-between', textAlign: 'left',
                    cursor: 'pointer', fontFamily: 'inherit', color: INK, transition: 'background 140ms ease',
                  }}
                >
                  <span style={{ ...mono, color: on ? INK : INK_MUTED }}>{t.num}</span>
                  <span style={{ fontWeight: 800, fontSize: 'clamp(24px, 2.6vw, 30px)', lineHeight: 1.05, letterSpacing: '-0.03em' }}>
                    {t.title}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Detail panel — does not exist until a card is tapped */}
        {track && (
          <div
            ref={panelRef}
            key={track.key}
            style={{
              background: '#fff', borderRadius: 24, border: `1px solid ${HAIRLINE}`,
              overflow: 'hidden', display: 'grid',
              // Two columns once a slot has a real asset, one while it doesn't. An
              // empty image well looks like a bug; a missing one looks like a choice.
              gridTemplateColumns: track.image ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr',
              animation: 'hjRowIn 240ms ease-out',
            }}
          >
            {track.image && (
              <img src={track.image} alt="" loading="lazy"
                   style={{ width: '100%', height: '100%', minHeight: 300, objectFit: 'cover', display: 'block', background: '#efe5d8' }} />
            )}
            <div style={{ padding: '34px 30px', display: 'grid', gap: 18, alignContent: 'center' }}>
            <h2 style={{ ...h2, margin: 0, fontSize: 'clamp(26px, 3vw, 33px)' }}>{track.panelTitle}</h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
              {track.steps.map(st => (
                <li key={st} style={{ display: 'grid', gridTemplateColumns: '9px 1fr', gap: 12, alignItems: 'start' }}>
                  <span style={{ width: 9, height: 9, borderRadius: 999, background: YELLOW, marginTop: 7 }} />
                  <span style={{ fontSize: 16.5, lineHeight: 1.5, color: INK_BODY }}>{st}</span>
                </li>
              ))}
            </ul>
            <a
              href={track.ctaTo === 'talent' ? talentHref : PROJECT_TOOL}
              onClick={(e) => { e.preventDefault(); navigate(track.ctaTo === 'talent' ? talentHref : PROJECT_TOOL); }}
              style={{
                justifySelf: 'start', minHeight: 48, display: 'inline-flex', alignItems: 'center',
                padding: '0 22px', borderRadius: 999, background: YELLOW, color: INK,
                fontWeight: 800, fontSize: 15.5, textDecoration: 'none',
              }}
            >
              {track.cta} →
            </a>
            </div>
          </div>
        )}

        {/* The page's conversion point */}
        <section ref={agentRef} style={section}>
          <p style={{ ...mono, color: INK_MUTED, margin: 0 }}>However you start</p>
          <h2 style={h2}>You talk. It builds the setup.</h2>
          <p style={{ fontSize: 17, lineHeight: 1.55, color: INK_BODY, margin: '12px 0 0', maxWidth: 640 }}>
            Describe the production in plain language. Hey Joe writes the breakdown, the roles,
            and the requirements.
          </p>
          {/* design slot: hmc-agent — the casting-agent embed belongs here */}
          <div style={{
            marginTop: 18, background: '#fff', borderRadius: 24, border: `1px solid ${HAIRLINE}`,
            padding: '30px 28px', display: 'grid', gap: 16, justifyItems: 'start',
          }}>
            <p style={{ fontSize: 17, lineHeight: 1.55, color: INK_BODY, margin: 0, maxWidth: 560 }}>
              Katia builds the brief with you, then posts the project. Once it&rsquo;s approved you get a
              shareable casting page, and submissions land in your casting studio instead of your inbox.
            </p>
            <a
              href={PROJECT_TOOL}
              onClick={(e) => { e.preventDefault(); navigate(PROJECT_TOOL); }}
              style={{
                minHeight: 48, display: 'inline-flex', alignItems: 'center', padding: '0 22px',
                borderRadius: 999, background: YELLOW, color: INK, fontWeight: 800, fontSize: 15.5, textDecoration: 'none',
              }}
            >
              Start a project →
            </a>
          </div>
        </section>

        {/* Then it keeps going */}
        <section style={section}>
          <h2 style={h2}>Then it keeps going.</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginTop: 18 }}>
            {[
              ['Live callbacks', 'Waiting room, one at a time, recorded.'],
              ['Readers on demand', 'Booked through the platform.'],
              ['Tapes stay put', 'With the role, not in your inbox.'],
            ].map(([title, line]) => (
              <div key={title} style={{ background: '#fff', borderRadius: 18, border: `1px solid ${HAIRLINE}`, padding: '22px 22px 24px' }}>
                {/* design slots: hmc-callback / hmc-reader / hmc-tapes */}
                <p style={{ margin: 0, fontWeight: 800, fontSize: 18, letterSpacing: '-0.02em', color: INK }}>{title}</p>
                <p style={{ margin: '6px 0 0', fontSize: 15, lineHeight: 1.5, color: INK_BODY }}>{line}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Production — dark */}
        <section style={{ ...section, background: INK, borderRadius: 24, padding: '40px 34px' }}>
          <p style={{ ...mono, color: YELLOW, margin: 0 }}>Production</p>
          <h2 style={{ ...h2, color: CREAM }}>
            Sometimes the callback <Swipe>is</Swipe> the production.
          </h2>
          {/* design slot: hmc-remote-shoot */}
          <p style={{ fontSize: 17, lineHeight: 1.55, color: CREAM, opacity: 0.85, margin: '14px 0 0', maxWidth: 640 }}>
            Remote-directed interviews, panels, hybrid events. We run the shoot wherever the people are.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20 }}>
            {['Interviews', 'Testimonials', 'Panels', 'Hybrid events', 'Private streams'].map(p => (
              <span key={p} style={pill(false)}>{p}</span>
            ))}
          </div>
        </section>

        {/* Post-production */}
        <section style={section}>
          <h2 style={h2}>And the work after the camera stops.</h2>
          <p style={{ fontSize: 17, lineHeight: 1.55, color: INK_BODY, margin: '12px 0 0', maxWidth: 640 }}>
            Not a Premiere replacement. The routine jobs, on footage already in the project.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20 }}>
            {['Transcribe', 'Caption', 'Trim', 'Stitch', 'Compress', 'Rotate', 'Audio cleanup', 'Slates'].map(p => (
              <span key={p} style={pill(true)}>{p}</span>
            ))}
          </div>
        </section>

        {/* Booth */}
        {SHOW_SPOTLIGHT && (
          <section style={{
            ...section, background: INK, borderRadius: 24, overflow: 'hidden', display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', alignItems: 'stretch',
          }}>
            {/* design slot: hmc-booth — the stand on the floor at HMC 2026 */}
            <img src="/brand/hmc-booth.jpg" alt="The Hey Joe stand at the Houston Media Conference"
                 loading="lazy"
                 style={{ width: '100%', height: '100%', minHeight: 320, maxHeight: 460, objectFit: 'cover', display: 'block' }} />
            <div style={{ padding: '40px 34px', display: 'grid', gap: 12, alignContent: 'center' }}>
              <h2 style={{ ...h2, color: CREAM, margin: 0 }}>Come to our booth at Houston Media Conference.</h2>
              <p style={{ fontSize: 17, lineHeight: 1.55, color: CREAM, opacity: 0.85, margin: 0, maxWidth: 620 }}>
                {BOOTH_NUMBER ? `We're at booth ${BOOTH_NUMBER}. ` : ''}Let&rsquo;s record a clip together. It takes
                five minutes, and I&rsquo;ll share it with you if you want.
              </p>
            </div>
          </section>
        )}

        <footer style={{ ...section, paddingBottom: 60, display: 'grid', gap: 8, borderTop: `1px solid ${HAIRLINE}`, paddingTop: 24 }}>
          <p style={{ ...mono, color: INK_MUTED, margin: 0 }}>Houston, Sept 17–20 · Breaking Silos, Building Stories</p>
          <p style={{ fontSize: 13, lineHeight: 1.55, color: INK_MUTED, margin: 0, maxWidth: 680 }}>
            Presented by Houston Cinema Arts Society and Southwest Alternate Media Project,
            with the Houston Film Commission and Houston Arts Alliance.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default HoustonProducersPage;
